import * as types from '../constants/ActionTypes';
import * as endPoints from '../constants/EndPoints';
import { fetchMiddleware } from './MiddlewareActions';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { translations as allTranslations } from '../translations/tr';

import { getDateForExcelExports } from './Utilities/dateUtils';
import { fetchMaintenanceReportSensors } from './DashboardActions';
import UIDateFormater, {
  DateType,
} from '../containers/UIhelper/UIDateFormater';

//--------------------------------
// General
//--------------------------------

// Helper function to translate report data
function translateReportData(data, translations, reportType) {
  if (!Array.isArray(data) || data.length === 0) return data;

  // Get column mappings based on report type
  const columnMappings = getReportColumnMappings(reportType, translations);
  const excludeProjectId =
    reportType === 'functionality' || reportType === 'reception';
  const excludeActionCode = reportType === 'sensorsOperations';

  return data.map((row) => {
    const translatedRow = {};
    Object.keys(row).forEach((key) => {
      if (
        excludeProjectId &&
        String(key)
          .replace(/[^a-z0-9]/gi, '')
          .toLowerCase() === 'projectid'
      ) {
        return;
      }

      if (
        excludeActionCode &&
        String(key)
          .replace(/[^a-z0-9]/gi, '')
          .toLowerCase() === 'actioncode'
      ) {
        return;
      }

      // Try to find translation in multiple ways
      let translatedKey =
        columnMappings[key] ||
        translations[key] ||
        translations[key.toLowerCase()] ||
        translations[toSnakeCase(key)] ||
        key;

      let value = row[key];
      if (
        typeof value === 'string' &&
        (key.toLowerCase().includes('status') ||
          key.toLowerCase().includes('type'))
      ) {
        value =
          translations[value.toLowerCase()] || translations[value] || value;
      }

      value = formatReportCellValue(value, key, reportType);

      translatedRow[translatedKey] = value;
    });
    return translatedRow;
  });
}

// Helper function to convert camelCase to snake_case
function toSnakeCase(str) {
  return str
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .replace(/^_/, '');
}

function formatReportCellValue(value, key, reportType) {
  const normalizedKey = String(key).toLowerCase();
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string' &&
        value.trim() !== '' &&
        !Number.isNaN(Number(value))
      ? Number(value)
      : null;
  const yesNoFields = new Set([
    'white_noise',
    'low_noise',
    'no_radio',
    'low_battery',
    'radio_sync',
    'radio_white_noise',
  ]);

  if (
    reportType === 'functionality' ||
    reportType === 'reception' ||
    reportType === 'maintenance'
  ) {
    if (normalizedKey === 'status' && numericValue != null) {
      return numericValue === 1 ? 'Active' : 'Inactive';
    }

    if (yesNoFields.has(normalizedKey) && numericValue != null) {
      return numericValue === 1 ? 'Yes' : 'No';
    }

    if (normalizedKey === 'sample_time') {
      if (numericValue != null) {
        const epochMillis =
          numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
        return new Date(epochMillis).toLocaleString();
      }
    }

    if (normalizedKey === 'battery_value' && numericValue != null) {
      return numericValue.toFixed(3);
    }
  }

  if (
    reportType === 'sensorsOperations' &&
    normalizedKey === 'time' &&
    typeof value === 'string'
  ) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 19).replace('T', ' ');
    }
  }

  return value;
}

// Helper function to get report-specific column mappings
function getReportColumnMappings(reportType, translations) {
  const mappings = {
    alerts: {
      ID: translations.alert_id || 'Alert ID',
      AlertID: translations.alert_id || 'Alert ID',
      AlertDate: translations.alert_date || 'Alert Date',
      AlertStatus: translations.alert_status || 'Alert Status',
      AlertType: translations.alert_type || 'Alert Type',
      Status: translations.status || 'Status',
      Type: translations.type || 'Type',
      Address: translations.address || 'Address',
      Severity: translations.severity || 'Severity',
      ProjectID: translations.project_id || 'Project ID',
      ProjectName: translations.project_name || 'Project Name',
      LeakID: translations.leak_id || 'Leak ID',
      Comment: translations.comment || 'Comment',
      CreatedDate: translations.created_date || 'Created Date',
      UpdatedDate: translations.updated_date || 'Updated Date',
      Date: translations.date || 'Date',
    },
    sensors: {
      ID: translations.Sensor_id || translations.sensor_id || 'Sensor ID',
      SensorID: translations.Sensor_id || translations.sensor_id || 'Sensor ID',
      Sensor_ID:
        translations.Sensor_id || translations.sensor_id || 'Sensor ID',
      Name: translations.name || 'Name',
      Type: translations.type || 'Type',
      Status: translations.status || 'Status',
      InstallDate: translations.install_date || 'Install Date',
      LastActivity: translations.last_activity || 'Last Activity',
      Address: translations.address || 'Address',
      SerialNumber: translations.serial_number || 'Serial Number',
      Model: translations.model || 'Model',
      ProjectName: translations.project_name || 'Project Name',
      Date: translations.date || 'Date',
    },
    pressureSensors: {
      ID: translations.sensor_id || 'Sensor ID',
      SOPointID: translations.sop_id || 'SOP ID',
      aqs_logger_id: translations.aqs_logger_id || 'AQS Logger ID',
      sensor_installation_date:
        translations.sensor_install_date || 'Sensor Install Date',
      PointID: translations.point_id || 'Point ID',
      DmaID: translations.dma_id || 'DMA ID',
      LastUninstallReason:
        translations.last_uninstall_reason || 'Last Uninstall Reason',
      logger_id: translations.logger_id || 'Logger ID',
      CustomerID: translations.customer_id || 'Customer ID',
      logger_type: translations.logger_type || 'Logger Type',
      logger_installation_date:
        translations.logger_install_date || 'Logger Install Date',
      logger_last_active_time: translations.last_active || 'Last Active',
      manufacture: translations.manufacture || 'Manufacture',
      type_meaning: translations.type || 'Type',
      manufacture_name: translations.manufacturer || 'Manufacturer',
    },
    couples: {
      ID: translations.couple_id || 'Couple ID',
      CoupleID: translations.couple_id || 'Couple ID',
      Sensor1: translations.sensor_1 || 'Sensor 1',
      Sensor2: translations.sensor_2 || 'Sensor 2',
      Sensor1ID: translations.sensor_1_id || 'Sensor 1 ID',
      Sensor2ID: translations.sensor_2_id || 'Sensor 2 ID',
      Distance: translations.distance || 'Distance',
      Status: translations.status || 'Status',
      CreatedDate: translations.created_date || 'Created Date',
      UpdatedDate: translations.updated_date || 'Updated Date',
      Date: translations.date || 'Date',
    },
    noiseAlerts: {
      ID: translations.noise_alert_id || 'Noise Alert ID',
      Date: translations.date || 'Date',
      Type: translations.type || 'Type',
      Level: translations.level || 'Level',
      Duration: translations.duration || 'Duration',
      Status: translations.status || 'Status',
      SensorID: translations.Sensor_id || translations.sensor_id || 'Sensor ID',
      Comment: translations.comment || 'Comment',
    },
    maintenance: {
      SensorID: 'Sensor ID',
      sensor_id: 'Sensor ID',
      DeviceID: 'Device ID',
      device_id: 'Device ID',
      ProjectID: 'Project ID',
      project_id: 'Project ID',
      white_noise: 'White Noise',
      low_noise: 'Low Noise',
      no_radio: 'No Radio',
      low_battery: 'Battery Issues',
      sample_noise: 'Sample Noise',
      min_noise: 'Min Noise',
      battery_value: 'Battery Value',
      status: 'Status',
      Status: 'Status',
      cellular_problem: 'Cellular Problem',
      radio_white_noise: 'Radio White Noise',
      RSRP: 'RSRP',
      rsrp: 'RSRP',
      samples_last_week: 'Samples Last Week',
      sample_time: 'Sample Time',
      project_name: 'Project Name',
      radio_sync: 'Radio Synchronization Issues',
    },
    functionality: {
      SensorID: 'Sensor ID',
      Sensor_ID: 'Sensor ID',
      DeviceID: 'Device ID',
      ProjectID: 'Project ID',
      CustomerID: 'Customer ID',
      customer_id: 'Customer ID',
      white_noise: 'White Noise',
      low_noise: 'Low Noise',
      no_radio: 'No Radio',
      low_battery: 'Low Battery',
      sample_noise: 'Sample Noise',
      SampleNoise: 'Sample Noise',
      min_noise: 'Min Noise',
      battery_value: 'Battery Value',
      sample_time: 'Sample Time',
      SampleTime: 'Sample Time',
    },
    reception: {
      SensorID: 'Sensor ID',
      Sensor_ID: 'Sensor ID',
      DeviceID: 'Device ID',
      ProjectID: 'Project ID',
      CustomerID: 'Customer ID',
      customer_id: 'Customer ID',
      cellular_problem: 'Cellular Problem',
      RSRP: 'RSRP',
      rsrp: 'RSRP',
      samples_last_week: 'Samples Last Week',
      radio_white_noise: 'Radio White Noise',
      sample_time: 'Sample Time',
      SampleTime: 'Sample Time',
    },
    pressureAlerts: {
      ID: translations.id || 'ID',
      Date: translations.date || 'Date',
      Type: translations.type || 'Type',
      Status: translations.status || 'Status',
      Pressure: translations.pressure || 'Pressure',
      Location: translations.location || 'Location',
    },
    projectHistory: {
      ID: translations.id || 'ID',
      Date: translations.date || 'Date',
      Time: translations.time || 'Time',
      User: translations.user || 'User',
      Action: translations.action || 'Action',
      Description: translations.description || 'Description',
      Details: translations.details || 'Details',
      OldValue: translations.old_value || 'Old Value',
      NewValue: translations.new_value || 'New Value',
      EntityType: translations.entity_type || 'Entity Type',
      EntityID: translations.entity_id || 'Entity ID',
    },
    users: {
      UserName: translations.user_name || 'User Name',
      Email: translations.email || 'Email',
      CustomerName: translations.customer_name || 'Customer Name',
      FirstName: translations.first_name || 'First Name',
      LastName: translations.last_name || 'Last Name',
      PhoneNumber: translations.phone_number || 'Phone Number',
    },
  };

  // Add common mappings that apply to all report types
  const commonMappings = {
    ID: translations.id || 'ID',
    Date: translations.date || 'Date',
    Status: translations.status || 'Status',
    Type: translations.type || 'Type',
    Address: translations.address || 'Address',
    Comment: translations.comment || 'Comment',
    Name: translations.name || 'Name',
    Description: translations.description || 'Description',
    CreatedDate: translations.created_date || 'Created Date',
    UpdatedDate: translations.updated_date || 'Updated Date',
    User: translations.user || 'User',
    Action: translations.action || 'Action',
    Time: translations.time || 'Time',
    Value: translations.value || 'Value',
    OldValue: translations.old_value || 'Old Value',
    NewValue: translations.new_value || 'New Value',
    ProjectID: translations.project_id || 'Project ID',
    ProjectName: translations.project_name || 'Project Name',
  };

  // Merge specific mappings with common mappings
  const specificMappings = mappings[reportType] || mappings['default'] || {};
  return { ...commonMappings, ...specificMappings };
}

// Helper function to convert JSON to Excel and download
function downloadJsonAsExcel(
  jsonData,
  filename,
  translations,
  reportType,
  sheetName = 'Report'
) {
  try {
    // Translate the data
    const translatedData = translateReportData(
      jsonData,
      translations,
      reportType
    );

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert JSON to worksheet
    const ws = XLSX.utils.json_to_sheet(translatedData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    // Create blob and download
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);

    const tempElement = document.createElement('a');
    tempElement.href = url;
    tempElement.download = filename;
    document.body.appendChild(tempElement);
    tempElement.click();
    document.body.removeChild(tempElement);

    // Clean up
    window.URL.revokeObjectURL(url);
  } catch (error) {}
}

const getMaintenanceField = (sensor, fields) => {
  const field = fields.find(
    (key) => sensor?.[key] !== undefined && sensor?.[key] !== null
  );
  if(sensor[field] === 'NULL' || sensor[field] === '') return '--';
  return field ? sensor[field] : '--';
};

const formatMaintenanceYesNo = (value) => {
  if (value === '--' || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value === 0 ? 'No' : 'Yes';

  const normalizedValue = String(value).trim().toLowerCase();
  if (['true', 'yes', '1'].includes(normalizedValue)) return 'Yes';
  if (['false', 'no', '0'].includes(normalizedValue)) return 'No';
  return value;
};

const formatMaintenanceSampleTime = (value, timeZone, projectTimeZone) => {
  if (value === '--' || value === '' || value == null) return '--';

  const timestamp =
    typeof value === 'number' || /^\d+$/.test(String(value).trim())
      ? Number(value)
      : Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;

  return UIDateFormater(
    timestamp < 1000000000000 ? timestamp * 1000 : timestamp,
    DateType.DATE_AND_TIME,
    timeZone,
    projectTimeZone
  );
};

const getMaintenanceAction = (sensor) => {
  const batteryOrRadio = sensor.low_battery || sensor.no_radio;
  const audio = sensor.white_noise || sensor.low_noise;

  if (batteryOrRadio && audio) {
    return `Sensor Replacement + Tail Replacement`;
  }
  if (batteryOrRadio) return 'Sensor Replacement';
  if (audio) return 'Tail Replacement';
  return 'Need To Check';
};

const maintenanceExcelColumns = [
  { header: '#', key: '#', width: 4 },
  { header: 'Action', key: 'Action', width: 15 },
  { header: 'Sensor ID', key: 'Sensor ID', width: 8 },
  { header: 'Device ID', key: 'Device ID', width: 16 },
  { header: 'Status', key: 'Status', width: 8 },
  { header: 'Battery Value', key: 'Battery Value', width: 12 },
  { header: 'White Noise', key: 'White Noise', width: 10 },
  { header: 'Low Noise', key: 'Low Noise', width: 10 },
  { header: 'No Radio', key: 'No Radio', width: 10 },
  { header: 'Battery Issues', key: 'Battery Issues', width: 14 },
  { header: 'Radio White Noise', key: 'Radio White Noise', width: 16 },
  { header: 'Radio Sync Issues', key: 'Radio Sync Issues', width: 16 },
  { header: 'Samples Last Week', key: 'Samples Last Week', width: 16 },
  { header: 'Sample Time', key: 'Sample Time', width: 18 },
  { header: 'Address', key: 'Address', width: 40 },
];

const getMaintenanceTimeZoneLabel = (timeZone, projectTimeZone) => {
  const normalizedTimeZone = String(timeZone || 'gmt').toLowerCase();

  if (normalizedTimeZone === 'local') return 'Local';
  if (normalizedTimeZone === 'project') {
    return projectTimeZone ? `Project (${projectTimeZone})` : 'Project';
  }
  if (normalizedTimeZone === 'utc' || normalizedTimeZone === 'gmt') return 'GMT';

  return timeZone || 'GMT';
};

// This intentionally mirrors the Maintenance Dashboard table, except for its
// icon-only Issues column which has no useful spreadsheet representation.
const buildMaintenanceTableRows = (
  reportSensors,
  projectSensors,
  timeZone,
  projectTimeZone,
  translations
) =>
  reportSensors.map((sensor, index) => {
    const sensorId =
      sensor?.id || sensor?.ID || sensor?.SensorID || sensor?.sensor_id;
    const existingSensor =
      sensorId && projectSensors?.sensors && projectSensors?.indexMap
        ? projectSensors.sensors[projectSensors.indexMap[sensorId]]
        : null;
    const row = {
      ...existingSensor,
      ...sensor,
      id: existingSensor?.id || sensorId,
      SensorID:
        existingSensor?.SensorID ||
        sensor?.SensorID ||
        sensor?.sensor_id ||
        sensorId,
      DeviceID:
        sensor?.DeviceID || sensor?.device_id || existingSensor?.DeviceID,
    };
    const batteryValue = getMaintenanceField(row, [
      'BatteryValue',
      'Battery Value',
      'batteryValue',
      'battery_value',
      'Battery',
      'battery',
    ]);
    const numericBatteryValue = Number(batteryValue);

    return {
      '#': index + 1,
      Action: row.action || getMaintenanceAction(row),
      'Sensor ID': getMaintenanceField(row, [
        'SensorID',
        'Sensor ID',
        'sensorId',
        'sensor_id',
        'id',
        'ID',
      ]),
      'Device ID': getMaintenanceField(row, [
        'DeviceID',
        'Device ID',
        'deviceId',
        'device_id',
      ]),
      Status: row.inactive ? 'Inactive' : 'Active',
      'Battery Value': Number.isFinite(numericBatteryValue)
        ? numericBatteryValue.toFixed(2)
        : batteryValue,
      'White Noise': formatMaintenanceYesNo(
        getMaintenanceField(row, [
          'WhiteNoise',
          'White Noise',
          'whiteNoise',
          'white_noise',
        ])
      ),
      'Low Noise': formatMaintenanceYesNo(
        getMaintenanceField(row, [
          'LowNoise',
          'Low Noise',
          'lowNoise',
          'low_noise',
        ])
      ),
      'No Radio': formatMaintenanceYesNo(
        getMaintenanceField(row, ['NoRadio', 'No Radio', 'noRadio', 'no_radio'])
      ),
      'Battery Issues': formatMaintenanceYesNo(
        getMaintenanceField(row, [
          'LowBattery',
          'Low Battery',
          'lowBattery',
          'low_battery',
        ])
      ),
      'Radio White Noise': formatMaintenanceYesNo(
        getMaintenanceField(row, [
          'RadioWhiteNoise',
          'Radio White Noise',
          'radioWhiteNoise',
          'radio_white_noise',
        ])
      ),
      'Radio Sync Issues': formatMaintenanceYesNo(
        getMaintenanceField(row, ['radio_sync'])
      ),
      'Samples Last Week': getMaintenanceField(row, [
        'SamplesLastWeek',
        'Samples Last Week',
        'samplesLastWeek',
        'samples_last_week',
      ]),
      'Sample Time': formatMaintenanceSampleTime(
        getMaintenanceField(row, [
          'SampleTime',
          'Sample Time',
          'sampleTime',
          'sample_time',
          'LastSampleTime',
          'last_sample_time',
        ]),
        timeZone,
        projectTimeZone
      ),
      Address: getMaintenanceField(row, ['address', 'Address']),
    };
  });

async function downloadMaintenanceTableAsExcel(
  rows,
  filename,
  timeZone,
  projectTimeZone
) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Maintenance');
  worksheet.columns = maintenanceExcelColumns;
  worksheet.addRows(rows);

  const addressColumn = worksheet.getColumn('Address');
  addressColumn.eachCell((cell) => {
    cell.alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
    };
  });
  addressColumn.alignment = {
    horizontal: 'left',
    vertical: 'middle',
    wrapText: true,
  };

  worksheet.addRow([]);
  const timezoneNoteRow = worksheet.addRow([
    `Times are in ${getMaintenanceTimeZoneLabel(timeZone, projectTimeZone)} time`,
  ]);
  timezoneNoteRow.getCell(1).font = {
    italic: true,
    size: 10,
    color: { argb: '666666' },
  };
  timezoneNoteRow.getCell(1).alignment = {
    horizontal: 'left',
    vertical: 'middle',
  };
  worksheet.mergeCells(
    timezoneNoteRow.number,
    1,
    timezoneNoteRow.number,
    maintenanceExcelColumns.length
  );

  const excelBuffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadReport(type, all, downloadType) {
  return (dispatch, getState) => {
    const state = getState();

    let path = endPoints.REPORTS_ENDPOINT + '/' + type;
    const { user } = state.user;

    let filters = [];
    let sort = {};
    switch (type) {
      case 'alerts':
        Object.assign(filters, state.leaksList.filters.filters);
        sort = state.leaksList.filters.sort;
        break;
      case 'noiseAlerts':
        Object.assign(filters, state.leaksList.filters.noiseAlerts.filters);
        sort = state.noiseAlerts.sortBy;
        break;
      case 'sensors':
        Object.assign(filters, state.leaksList.filters.sensorsFilters);
        sort = state.leaksList.filters.sensorsSort;
        break;

      case 'couples':
        filters = [...state.leaksList.filters.couples.filters];
        sort = { ...state.leaksList.filters.couples.sort };
        break;

      case 'assessments':
        filters = [
          {
            field: 'CoupleID',
            value: [state.leaksList.leaksByProject.selectedFeature],
          },
        ];
        break;
      case 'history': {
        const workingState = state.leaksList.leaksByProject;
        const value =
          workingState.selectedFeatureType == 'couple'
            ? workingState.selectedAssessment
            : workingState.selectedFeature;
        filters = [
          {
            field: 'LeakID',
            value: [value],
          },
        ];
        break;
      }
      case 'projectHistory':
        Object.assign(filters, state.leaksList.filters.filters);
        break;
      case 'sops':
        Object.assign(filters, state.leaksList.filters.sops.filters);
        break;

      case 'interferences':
        Object.assign(filters, state.leaksList.filters.interferencesFilters);
        Object.assign(sort, state.leaksList.filters.interferencesSort);
        break;

      default:
        break;
    }

    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const projectDetails =
      projectsList.items[projectsList.projectIndexMap[projectId]];
    if (!all) {
      const projectFilter = {
        field: 'project_id',
        value: [projectId],
        cityColumn: projectDetails.IsDisplayCityColumn,
      };
      filters.push(projectFilter);
    }
    path += `/0?filters=${JSON.stringify(filters)}&sort=${JSON.stringify(
      sort
    )}${
      downloadType ? `&type=${downloadType}` : ''
    }&time=${getDateForExcelExports()}`;

    // const token = user.refreshToken;
    // let char = "&";
    // if (path.indexOf("?") === -1) {
    //     char = "?";
    // }
    // path += char + "token=" + token;
    // downloadFileFromURl(path);
    // window.location = path;

    // Always fetch JSON data first, then convert to Excel if needed
    const jsonPath = path.replace(/&type=[^&]*/, '') + '&type=json';

    // if (downloadType === 'json') {
    //   return fetchMiddleware(path, {}, getState);
    // } else {
    //   user.getIdToken().then((token) => {
    //     let char = "&";
    //     if (path.indexOf("?") === -1) {
    //       char = "?";
    //     }
    //     path += char + "token=" + token;
    //     downloadFileFromURl(path);
    //     // window.location = path;
    //   });
    // }
    // return;

    return fetchMiddleware(jsonPath, {}, getState)
      .then((response) => {
        if (response && response.data) {
          // Get current language and translations
          const currentLang = state.i18nState.lang || 'en';
          // Get translations from imported translations object
          const translations =
            allTranslations[currentLang] || allTranslations.en || {};

          // Generate filename with timestamp
          const timestamp = getDateForExcelExports();
          const filename = `${projectDetails.ShortName}_${type}_report_${timestamp}.xlsx`;

          // Convert JSON to Excel and download
          downloadJsonAsExcel(response.data, filename, translations, type);
        }
        return response;
      })
      .catch((error) => {
        console.error('Error downloading report:', error);
        throw error;
      });
  };
}

// Exports the users table as displayed (after local text filtering), so the
// rows are received from the component instead of being fetched by filters.
export const downloadUsersReport = (users) => (dispatch, getState) => {
  const state = getState();
  const currentLang = state.i18nState.lang || 'en';
  const translations = allTranslations[currentLang] || allTranslations.en || {};

  const rows = (users || []).map((user) => ({
    UserName: user.UserName,
    Email: user.Email,
    CustomerName: user.CustomerName,
    FirstName: user.FirstName,
    LastName: user.LastName,
    PhoneNumber: user.PhoneNumber,
  }));

  const filename = `users_report_${getDateForExcelExports()}.xlsx`;
  downloadJsonAsExcel(rows, filename, translations, 'users');
};

export const downloadSensorsReportFromPolygon =
  (sensors) => (dispatch, getState) => {
    const state = getState();
    const filters = [...state.leaksList.filters.sensorsFilters];
    const sort = { ...state.leaksList.filters.sensorsSort };

    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const projectDetails =
      projectsList.items[projectsList.projectIndexMap[projectId]];
    const projectFilter = {
      field: 'project_id',
      value: [projectId],
      cityColumn: projectDetails.IsDisplayCityColumn,
    };
    filters.push(projectFilter);

    filters.push({
      field: 'ID',
      op: 'list',
      value: sensors.map((sensor) => sensor.id),
    });

    const url = `${
      endPoints.REPORTS_ENDPOINT
    }/sensors/0?filters=${JSON.stringify(filters)}&sort=${JSON.stringify(
      sort
    )}&type=json`;

    return fetchMiddleware(url, {}, getState)
      .then((response) => {
        if (response && response.data) {
          const currentLang = state.i18nState.lang || 'en';
          const translations =
            allTranslations[currentLang] || allTranslations.en || {};
          const timestamp = getDateForExcelExports();
          const filename = `sensors_report_${timestamp}.xlsx`;
          downloadJsonAsExcel(response.data, filename, translations, 'sensors');
        }
        return response;
      })
      .catch((error) => {
        console.error('Error downloading sensors report:', error);
        throw error;
      });
  };

export const downloadPressureSensorsReport = () => (dispatch, getState) => {
  const state = getState();
  const projectId = state.leaksList.selectedProject;
  const projectsList = state.leaksList.projectsList;
  const projectDetails =
    projectsList.items[projectsList.projectIndexMap[projectId]];

  // Get pressure sensors filters if they exist
  const filters = [];

  // Fetch from pressure sensors API
  const url = `${
    endPoints.SENSORS_PRESSURE_ENDPOINT
  }/${projectId}?filters=${JSON.stringify(filters)}`;

  return fetchMiddleware(url, {}, getState)
    .then((response) => {
      // Handle response - it can be an array directly or { status: true, data: [...] }
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (response && response.status && response.data) {
        data = Array.isArray(response.data) ? response.data : [response.data];
      } else if (response && response.data) {
        data = Array.isArray(response.data) ? response.data : [response.data];
      }

      if (data.length > 0) {
        const currentLang = state.i18nState.lang || 'en';
        const translations =
          allTranslations[currentLang] || allTranslations.en || {};
        const timestamp = getDateForExcelExports();
        const filename = `${projectDetails.ShortName}_pressure_sensors_report_${timestamp}.xlsx`;

        downloadJsonAsExcel(data, filename, translations, 'pressureSensors');
      }
      return response;
    })
    .catch((error) => {
      console.error('Error downloading pressure sensors report:', error);
      throw error;
    });
};

//--------------------------------
// Alerts related Reports
//--------------------------------
export const downloadPressureAlertsReport = (allProject) => {
  return (dispatch, getState) => {
    const state = getState();

    const from = state.leaksList.pressure.transientAlerts.datesRange.from;
    const to = state.leaksList.pressure.transientAlerts.datesRange.to;
    const projectId = state.leaksList.selectedProject;

    const urlCode = allProject
      ? 'prsTransientResultsReport'
      : 'prsTransientReport';

    let url = `${endPoints.PRESSURE_ENDPOINT}/${urlCode}/${projectId}?from=${from}&to=${to}&type=json`;

    return fetchMiddleware(url, {}, getState)
      .then((response) => {
        if (response && response.data) {
          const currentLang = state.i18nState.lang || 'en';
          const translations =
            allTranslations[currentLang] || allTranslations.en || {};
          const timestamp = getDateForExcelExports();
          const filename = `pressure_alerts_report_${timestamp}.xlsx`;

          // Use a generic mapping for pressure alerts
          const translatedData = translateReportData(
            response.data,
            translations,
            'pressureAlerts'
          );
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(translatedData);
          XLSX.utils.book_append_sheet(wb, ws, 'Pressure Alerts');
          const excelBuffer = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'array',
          });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const blobUrl = window.URL.createObjectURL(blob);

          const tempElement = document.createElement('a');
          tempElement.href = blobUrl;
          tempElement.download = filename;
          document.body.appendChild(tempElement);
          tempElement.click();
          document.body.removeChild(tempElement);
          window.URL.revokeObjectURL(blobUrl);
        }
        return response;
      })
      .catch((error) => {
        console.error('Error downloading pressure alerts report:', error);
        throw error;
      });
  };
};

//--------------------------------
// Sensors related Reports
//--------------------------------
const downloadSensorSpecialReport = ({
  reportKey,
  reportType,
  filenamePrefix,
  projectName,
  callback,
  sheetName,
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const projectId = state.leaksList.selectedProject;
    const projectIdentifier = projectId;

    if (!projectIdentifier) {
      const error = new Error(`Project id is required for ${reportKey} report`);
      if (callback) {
        callback(error, false);
      }
      return error;
    }

    const baseUrl = `${endPoints.END_POINT}/sensors/${reportKey}/${projectIdentifier}`;

    return fetchMiddleware(`${baseUrl}?type=check`, {}, getState)
      .then((json) => {
        if (!json?.status) {
          if (callback) {
            callback(
              new Error(
                json?.data?.message ||
                  json?.data ||
                  `Failed to check ${reportKey} report availability`
              ),
              false
            );
          }
          return json;
        }

        if (!json?.data?.haveData) {
          if (callback) {
            callback(null, false);
          }
          return json;
        }

        if (callback) {
          callback(null, true);
        }

        return fetchMiddleware(`${baseUrl}?type=json`, {}, getState)
          .then((response) => {
            if (response && response.data) {
              const currentLang = state.i18nState.lang || 'en';
              const translations =
                allTranslations[currentLang] || allTranslations.en || {};
              const timestamp = getDateForExcelExports();
              const hasProjectName =
                projectName &&
                filenamePrefix
                  .toLowerCase()
                  .startsWith(`${String(projectName).toLowerCase()}_`);
              const projectFilenamePrefix =
                projectName && !hasProjectName
                  ? `${projectName}_${filenamePrefix}`
                  : filenamePrefix;
              const filename = `${projectFilenamePrefix}_${timestamp}.xlsx`;
              downloadJsonAsExcel(
                response.data,
                filename,
                translations,
                reportType,
                sheetName
              );
            }
            return response;
          })
          .catch((error) => {
            console.error(`Error downloading ${reportKey} report:`, error);
            if (callback) {
              callback(error, false);
            }
            return error;
          });
      })
      .catch((error) => {
        console.error(
          `Error checking ${reportKey} report availability:`,
          error
        );
        if (callback) {
          callback(error, false);
        }
        return error;
      });
  };
};

export const downloadSensorsMaintenanceReport =
  (callback, filenamePrefix = 'maintenance_report') =>
  (dispatch, getState) => {
    const state = getState();
    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const project =
      projectsList?.items?.[projectsList?.projectIndexMap?.[projectId]] ||
      projectsList?.items?.find(
        (item) => String(item?.ID) === String(projectId)
      );
    const projectName = project?.ShortName || 'project';
    const timestamp = getDateForExcelExports();
    const filename = `${filenamePrefix}_${timestamp}_${projectName}.xlsx`;

    if (!projectId) {
      const error = new Error('Project id is required for maintenance report');
      if (callback) {
        callback(error, false);
      }
      return Promise.resolve(error);
    }

    return dispatch(fetchMaintenanceReportSensors(projectId))
      .then((response) => {
        if (!response?.status) {
          const error = new Error(
            response?.err?.message ||
              response?.err ||
              response?.data ||
              'Failed to fetch maintenance report sensors'
          );
          if (callback) callback(error, false);
          return response;
        }

        const currentState = getState();
        const reportSensors =
          currentState.leaksList.dashboard.maintenanceReportSensors.data || [];
        if (!reportSensors.length) {
          if (callback) callback(null, false);
          return response;
        }

        const currentProject =
          currentState.leaksList.projectsList.items[
            currentState.leaksList.projectsList.projectIndexMap[projectId]
          ] || project;
        const selectedTimeZone = currentState.local?.timeZone;
        const projectTimeZone =
          currentProject?.TimezoneName ||
          currentProject?.TimeZoneName ||
          currentProject?.timezone;
        const rows = buildMaintenanceTableRows(
          reportSensors,
          currentState.leaksList.leaksByProject?.[projectId]?.sensors,
          selectedTimeZone,
          projectTimeZone
        );

        return downloadMaintenanceTableAsExcel(
          rows,
          filename,
          selectedTimeZone,
          projectTimeZone
        ).then(() => {
          if (callback) callback(null, true);
          return response;
        });
      })
      .catch((error) => {
        if (callback) callback(error, false);
        return error;
      });
  };

// The legacy Sensors Operations report is generated by the sensors service.
// Keep it separate from the dashboard maintenance report above: although the
// buttons look similar, they contain different data.
export const downloadSensorsOperationsReport =
  (callback) => (_dispatch, getState) => {
    const state = getState();
    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const project = projectsList.items[projectsList.projectIndexMap[projectId]];
    if (!projectId) {
      const error = new Error(
        'Project id is required for sensors operations report'
      );
      if (callback) callback(error, false);
      return Promise.resolve(error);
    }

    const projectName = project?.ShortName || projectId;
    const filename = `sensors_operations_${getDateForExcelExports()}_${projectName}.xlsx`;

    // This endpoint now responds with JSON, not an XLSX attachment. Fetch it
    // with the standard authenticated middleware and convert the payload into
    // the same client-side Excel format used by the other reports.
    const url = `${
      endPoints.END_POINT
    }/sensors/maintenance/${encodeURIComponent(projectId)}`;
    return fetchMiddleware(url, {}, getState)
      .then((response) => {
        if (response?.data) {
          const currentLang = state.i18nState.lang || 'en';
          const translations =
            allTranslations[currentLang] || allTranslations.en || {};
          downloadJsonAsExcel(
            response.data,
            filename,
            translations,
            'sensorsOperations',
            'Sensors Operations'
          );
        }
        if (callback) callback(null, true);
      })
      .catch((error) => {
        if (callback) callback(error, false);
        return error;
      });
  };

export const downloadSensorsFunctionalityReport =
  (callback) => (dispatch, getState) => {
    const state = getState();

    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const projectDetails =
      projectsList.items[projectsList.projectIndexMap[projectId]];
    const projectName = projectDetails?.ShortName || projectDetails?.Name;
    return dispatch(
      downloadSensorSpecialReport({
        reportKey: 'functionality',
        reportType: 'functionality',
        filenamePrefix: 'sensors_functionality_report',
        projectName,
        callback,
        sheetName: 'Functionality',
      })
    );
  };
export const downloadSensorsReceptionReport =
  (callback) => (dispatch, getState) => {
    const state = getState();

    const projectId = state.leaksList.selectedProject;
    const projectsList = state.leaksList.projectsList;
    const projectDetails =
      projectsList.items[projectsList.projectIndexMap[projectId]];
    const projectName = projectDetails?.ShortName || projectDetails?.Name;
    return dispatch(
      downloadSensorSpecialReport({
        reportKey: 'reception',
        reportType: 'reception',
        filenamePrefix: 'sensors_reception_report',
        projectName,
        callback,
        sheetName: 'Reception',
      })
    );
  };
//--------------------------------
// Couples related Reports
//--------------------------------

//--------------------------------
// Reports Menu
//--------------------------------
export function fetchReport(type, projects) {
  return (dispatch, getState) => {
    const state = getState();
    let filters = [];
    switch (type) {
      case 'alerts':
        filters = [Object.assign({}, state.leaksList.filters.filters[0])];
        break;
    }
    if (projects && projects.length > 0) {
      filters.push({
        field: 'ProjectID',
        op: '=',
        value: projects,
      });
    }
    const filtersStr = JSON.stringify(filters);
    const path =
      endPoints.END_POINT + '/type/test/report?filters=' + filtersStr;
    dispatch(requestReport(type, filters));
    return fetchMiddleware(path, {}, getState).then((json) => {
      dispatch(receiveReport(type, filters, json.data));
    });
  };
}

function requestReport(reportType, filters) {
  return {
    type: types.REQUEST_REPORT,
    reportType,
    filters,
  };
}

function receiveReport(reportType, filters, report) {
  return {
    type: types.RECEIVE_REPORT,
    reportType,
    filters,
    report,
  };
}

export const fetchPipeAssessmentsReport = () => {
  return (dispatch, getState) => {
    const state = getState();
    const projectId = state.leaksList.selectedProject;
    const path = endPoints.PIPE_ASSESSMENTS_ENDPOINT + '/report/' + projectId;

    dispatch(assessmentsReportRequest());
    return fetchMiddleware(path, {}, getState).then((json) => {
      let data = [];
      if (json.status) {
        data = json.data;
      }
      return dispatch(assessmentsReportReceive(data));
    });
  };
};

const assessmentsReportRequest = () => {
  return {
    type: types.ASSESSMENTS_REPORT_REQUEST,
  };
};

const assessmentsReportReceive = (records) => {
  return {
    type: types.ASSESSMENTS_REPORT_RECEIVE,
    records,
  };
};
